// src/pages/UserManagement.js
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import { can } from '../utils/permissions';
import { getCurrentUser } from '../utils/auth';
import { confirmDialog, alertDialog } from '../utils/dialog';
import Table from '../components/Table';
import UserForm from '../components/UserForm';
import {
  PlusIcon, EditIcon, TrashIcon, SearchIcon, UsersIcon, AlertTriangleIcon,
} from '../components/Icon';

export default function UserManagement() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [search, setSearch]   = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | user object

  const currentUser = getCurrentUser();
  const canCreate = can(currentUser, 'users.create');
  const canEdit   = can(currentUser, 'users.edit');
  const canDelete = can(currentUser, 'users.delete');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.users();
      setRows(res.users || []);
      setError(false);
    } catch {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (u) => {
    const next = u.is_active === 1 || u.is_active === '1' ? 0 : 1;
    const res = await api.userToggle(u.id, next);
    if (res?.success) load();
    else alertDialog({ title: 'Update Failed', message: res?.error || 'Could not update status', tone: 'danger' });
  };

  const handleDelete = async (u) => {
    const ok = await confirmDialog({
      title: 'Delete User',
      message: `Delete user "${u.username}"?\nThis cannot be undone.`,
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    const res = await api.userDelete(u.id);
    if (res?.success) load();
    else alertDialog({ title: 'Delete Failed', message: res?.error || 'Delete failed', tone: 'danger' });
  };

  const handleSaved = () => { setEditing(null); load(); };

  const filtered = rows.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.username?.toLowerCase().includes(s) || u.name?.toLowerCase().includes(s) || u.role?.toLowerCase().includes(s) || u.mobile?.includes(s);
  });

  const activeCount = rows.filter(u => u.is_active === 1 || u.is_active === '1').length;

  const columns = [
    { key: 'username', header: 'Username', nowrap: true,
      tdStyle: { fontWeight: 700, color: '#1a1a1a' }, render: (u) => u.username },
    { key: 'name', header: 'Name', render: (u) => u.name || <span className="tbl-dash">—</span> },
    { key: 'mobile', header: 'Mobile', nowrap: true, render: (u) => u.mobile || <span className="tbl-dash">—</span> },
    { key: 'role', header: 'Role', render: (u) => (
      <span className={`pill pill--role pill--${(u.role || '').toLowerCase() || 'default'}`}>{u.role}</span>
    ) },
    { key: 'status', header: 'Status', align: 'center', render: (u) => {
      const active = u.is_active === 1 || u.is_active === '1';
      return (
        <label className={`toggle${active ? ' is-on' : ''}`} title={canEdit ? (active ? 'Click to deactivate' : 'Click to activate') : 'No permission'}>
          <input type="checkbox" className="toggle-input" checked={active} disabled={!canEdit} onChange={() => handleToggle(u)} />
          <span className="toggle-dot" />
        </label>
      );
    } },
    { key: 'created_at', header: 'Created', nowrap: true,
      tdStyle: { color: '#888', fontSize: 12 }, render: (u) => u.created_at?.split(' ')[0] || '—' },
    { key: 'actions', header: 'Actions', nowrap: true, align: 'right', render: (u) => (
      <>
        {canEdit && (
          <button className="icon-btn icon-btn--edit" onClick={() => setEditing(u)} title="Edit" aria-label="Edit"><EditIcon size={13} /></button>
        )}
        {canDelete && (
          <button className="icon-btn icon-btn--del" onClick={() => handleDelete(u)} title="Delete" aria-label="Delete"><TrashIcon size={13} color="#c0392b" /></button>
        )}
      </>
    ) },
  ];

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <div className="page-sub">{rows.length} total · {activeCount} active</div>
        </div>
        {canCreate && (
          <button className="btn-primary" onClick={() => setEditing('new')}>
            <PlusIcon size={14} /> <span>Add User</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="toolbar">
        <div className="search-wrap">
          <SearchIcon size={14} className="search-icon" />
          <input
            className="search-input"
            placeholder="Search by username, name or role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {search && <button className="reset-btn" onClick={() => setSearch('')}>Reset</button>}
      </div>

      {/* Content */}
      <div className="table-card">
        {loading ? (
          <div className="loader-wrap"><div className="spinner" /></div>
        ) : error ? (
          <div className="empty-state">
            <div><AlertTriangleIcon size={42} color="#c0392b" /></div>
            <div className="um-err-title">Cannot load users</div>
            <div className="um-err-sub">Check the API connection.</div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={filtered}
            rowKey={(u) => u.id}
            minWidth={720}
            maxHeight="62vh"
            emptyMessage={
              <div>
                <div className="empty-icon-muted"><UsersIcon size={40} /></div>
                <div className="empty-sub">No users found</div>
              </div>
            }
          />
        )}
      </div>

      {editing && (
        <UserForm
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
